/* The canvas, and the HTML tooltip that hovers over it. Chart.js's own tooltip is drawn on
 * the canvas and cannot lay out the label/value rows the panels build, so rendering is handed
 * back to React through its `external` hook.
 */

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import type { ChartConfiguration } from 'chart.js/auto';

import { Chart } from './chart';

export interface TooltipRow {
  label: string;
  value: string;
  /** A swatch before the label, for a chart with more than one series. */
  color?: string;
}

export interface TooltipContent {
  title: string;
  rows: TooltipRow[];
}

/** What a panel is handed for the point under the cursor, deliberately not a Chart.js
 *  type. */
export interface ChartPoint {
  label: string;
  value: number | null;
  dataIndex: number;
  datasetIndex: number;
}

interface Props {
  /** The plot's height in pixels. `maintainAspectRatio: false` means Chart.js takes its
   *  height from the container, and a container with none collapses to zero. */
  height: number;
  /** Memoise this at the call site — a new object rebuilds the chart. */
  config: ChartConfiguration;
  tooltip?: (points: ChartPoint[]) => TooltipContent | null;
  /** Announced to a screen reader in place of the canvas. */
  label: string;
}

interface Placed {
  content: TooltipContent;
  x: number;
  y: number;
}

/* ------------------------------------------------------------------ the stall watch
 *
 * Chart.js gives up on a chart it believes has left the document. Its `detach` handler sets
 * `attached` false, stops the animator and wipes the canvas, and from then on `render()`
 * returns without drawing — for good, since only an `attach` puts the flag back, and that
 * only arrives if the canvas is returned to the position it was taken from. Anything that
 * re-parents it instead — a browser extension, a page translation rewriting the DOM around
 * it — leaves a chart that is alive, lays out its scales and answers the pointer over a plot
 * it will never draw again. A blank card with a working tooltip on it is that, and nothing
 * else in this file catches it: the guard below has already been stood down, the visibility
 * handlers ask whether the chart ever drew rather than whether it still can, and the box it
 * sits in never changed width.
 *
 * A re-parenting fires no event of its own, so the document is watched for one. One observer
 * for the page rather than one per chart: the question is the same for all of them, and
 * Chart.js already keeps two of these per chart, so this is not a new cost. The work per
 * mutation is two property reads per chart, and a chart that is drawing is left alone.
 */
const stalled = new Set<() => void>();
let watcher: MutationObserver | null = null;

function watchForStalls(check: () => void): () => void {
  stalled.add(check);
  if (!watcher) {
    watcher = new MutationObserver(() => stalled.forEach((fn) => fn()));
    watcher.observe(document, { childList: true, subtree: true });
  }
  return () => {
    stalled.delete(check);
    if (!stalled.size) {
      watcher?.disconnect();
      watcher = null;
    }
  };
}

export function ChartCanvas({ height, config, tooltip, label }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<Placed | null>(null);

  // Read by the external handler without re-creating the chart when only the closure
  // over `tooltip` changes.
  const tooltipRef = useRef(tooltip);
  tooltipRef.current = tooltip;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    /* Two guards against a blank plot: Chart.js measures at construction, so a chart built at
     * zero width lays every element out at x=0. `build` waits for the box to have a width,
     * and the observer below rescues a box that collapses and re-opens later.
     */
    const box = canvas.parentElement;
    let chart: Chart | null = null;
    let cancelled = false;
    let drawn = false;

    /* Chart.js paints its first frame from `requestAnimationFrame`, which a minimised,
     * occluded or backgrounded window never runs. The chart is alive by then — it has its
     * scales, and its tooltips answer the pointer — so the failure looks like a blank card
     * with a working chart behind it rather than like a chart that failed. This finishes the
     * job without waiting for a frame; `update('none')` first, so what lands is the final
     * picture rather than the state the entry animation would have started from.
     */
    const paint = () => {
      if (!chart || cancelled) return;
      // Re-attaching first, since `attached` gates the animator as well as `render()`: a
      // chart left detached would take the update and still put nothing on the canvas.
      if (!chart.attached && canvas.isConnected) {
        (chart as { attached: boolean }).attached = true;
        // The size was zeroed on the way out and has to be read off the box again.
        chart.resize();
      }
      chart.update('none');
      chart.draw();
    };

    /** Alive, in the document, and unable to draw — see the stall watch above. */
    const isStalled = (): boolean =>
      Boolean(chart) && !cancelled && canvas.isConnected && !chart!.attached;

    const build = () => {
      if (cancelled || chart) return;
      chart = new Chart(canvas, {
        ...config,
        // Appended rather than replacing whatever the caller declared.
        plugins: [
          ...(config.plugins ?? []),
          { id: 'firstPaint', afterDraw: () => { drawn = true; } },
        ],
        options: {
          ...config.options,
          plugins: {
            ...config.options?.plugins,
            tooltip: {
              enabled: false,
              external: ({ tooltip: model }) => {
                const build = tooltipRef.current;
                if (!build) return;
                if (!model.opacity || !model.dataPoints?.length) {
                  setTip((current) => (current ? null : current));
                  return;
                }
                const points: ChartPoint[] = model.dataPoints.map((p) => ({
                  label: String(p.label ?? ''),
                  value: typeof p.raw === 'number' ? p.raw : null,
                  dataIndex: p.dataIndex,
                  datasetIndex: p.datasetIndex,
                }));
                const content = build(points);
                if (!content) return;
                setTip({ content, x: model.caretX, y: model.caretY });
              },
            },
          },
        },
      });
    };

    if (!box || box.clientWidth > 0) build();

    /* The rescue above must not become the normal path: a page busy rendering a panel can
     * take longer than a timer to reach its first frame, and painting it by hand then would
     * cost every chart on the site its entry animation. So the question asked is not "has it
     * drawn yet" but "is this window producing frames at all" — one is proof enough, since
     * the chart's own paint is scheduled the same way and will land. A window that never
     * answers is one nobody is looking at, and it gets the picture drawn for it instead.
     */
    let guard = 0;
    const probe = requestAnimationFrame(() => {
      window.clearTimeout(guard);
    });
    guard = window.setTimeout(() => {
      if (!drawn) paint();
    }, 2000);

    // Observed rather than measured once: a box that is empty now may be open on the next
    // frame, and nothing else says when. The same observer covers both jobs — build the chart
    // on the first width it ever has, and resize one whose box collapsed and came back. An
    // ordinary width change is Chart.js's own to handle, and taking it over here would repaint
    // the chart mid-animation every time the page grows a scrollbar.
    let width = box?.clientWidth ?? 0;
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width ?? 0;
      if (next > 0) {
        if (!chart) build();
        else if (width === 0) chart.resize();
        else if (isStalled()) paint();
      }
      width = next;
    });
    if (box) observer.observe(box);

    // A canvas that came back into the document under a different parent, which Chart.js
    // does not recognise as the return of the one that left.
    const unwatch = watchForStalls(() => {
      if (isStalled()) paint();
    });

    /* A first paint is scheduled on requestAnimationFrame, which a background tab does not
     * run, and nothing re-schedules it when the tab comes back. `pageshow` covers the same
     * hole for a page restored from the back/forward cache. Only ever for a chart that cannot
     * draw as things stand — one that has never drawn, or one stalled by a detach: a canvas
     * keeps its pixels while the tab is away, so a chart that is merely idle needs nothing,
     * and repainting it here would cut short whatever it is animating.
     */
    const onVisible = () => {
      if ((!drawn || isStalled()) && document.visibilityState === 'visible') paint();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      cancelled = true;
      window.clearTimeout(guard);
      cancelAnimationFrame(probe);
      observer.disconnect();
      unwatch();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onVisible);
      window.removeEventListener('focus', onVisible);
      chart?.destroy();
      setTip(null);
    };
  }, [config]);

  // Clamped after layout rather than before: the width is only known once the rows are
  // in the DOM, and a tooltip half off the card is worse than one a few pixels off centre.
  useLayoutEffect(() => {
    const box = tipRef.current;
    const host = hostRef.current;
    if (!box || !host || !tip) return;
    const width = box.offsetWidth;
    const left = Math.max(0, Math.min(host.clientWidth - width, tip.x - width / 2));
    box.style.left = `${left}px`;
    box.style.top = `${Math.max(0, tip.y - box.offsetHeight - 10)}px`;
  }, [tip]);

  return (
    <div className="chart-host" ref={hostRef}>
      <div style={{ height }}>
        <canvas ref={canvasRef} role="img" aria-label={label} />
      </div>
      {tip && (
        <div className="chart-tooltip on" ref={tipRef} role="presentation">
          <div className="tt-title">{tip.content.title}</div>
          {tip.content.rows.map((row, i) => (
            <div className="tt-row" key={i}>
              {row.color && <span className="tt-swatch" style={{ background: row.color }} />}
              <span>{row.label}</span>
              <b>{row.value}</b>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** The swatch legend under a chart with more than one series. */
export function Legend({ items }: { items: { name: string; color: string }[] }): ReactNode {
  return (
    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
      {items.map((item) => (
        <span
          key={item.name}
          className="flex items-center gap-1.5 text-xs text-[var(--text-secondary)]"
        >
          <span
            className="h-2 w-2 rounded-full"
            style={{ background: item.color }}
            aria-hidden="true"
          />
          {item.name}
        </span>
      ))}
    </div>
  );
}
